import { useState } from 'react';
import { Folder, FolderOpen, File, ChevronRight, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import type { FileTreeNode } from '../types/wizard';

interface FileTreeProps {
  nodes: FileTreeNode[];
  selectedPath?: string;
  onSelect?: (path: string) => void;
}

interface TreeNodeProps {
  node: FileTreeNode;
  depth: number;
  selectedPath?: string;
  onSelect?: (path: string) => void;
}

function TreeNodeComponent({ node, depth, selectedPath, onSelect }: TreeNodeProps) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isFolder = node.type === 'directory';
  const isSelected = selectedPath === node.path;

  const handleClick = () => {
    if (isFolder) {
      setExpanded(!expanded);
    }
    onSelect?.(node.path);
  };

  const getFileIcon = (name: string) => {
    if (name.endsWith('.md')) return 'text-blue-400';
    if (name.endsWith('.json') || name.endsWith('.yml') || name.endsWith('.yaml'))
      return 'text-amber-400';
    if (name.endsWith('.ts') || name.endsWith('.tsx')) return 'text-blue-400';
    if (name.endsWith('.js') || name.endsWith('.jsx')) return 'text-yellow-400';
    if (name.endsWith('.py')) return 'text-green-400';
    if (name.endsWith('.sh')) return 'text-green-300';
    return 'text-gray-500';
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={clsx(
          'flex items-center gap-1.5 w-full text-left px-2 py-1 rounded-md text-sm transition-colors',
          isSelected
            ? 'bg-accent/10 text-accent-300'
            : 'text-gray-400 hover:bg-surface-100/50 hover:text-gray-300'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {isFolder ? (
          <>
            {expanded ? (
              <ChevronDown className="h-3 w-3 text-gray-500 shrink-0" />
            ) : (
              <ChevronRight className="h-3 w-3 text-gray-500 shrink-0" />
            )}
            {expanded ? (
              <FolderOpen className="h-4 w-4 text-accent shrink-0" />
            ) : (
              <Folder className="h-4 w-4 text-accent shrink-0" />
            )}
          </>
        ) : (
          <>
            <span className="w-3 shrink-0" />
            <File className={clsx('h-4 w-4 shrink-0', getFileIcon(node.name))} />
          </>
        )}
        <span className="truncate">{node.name}</span>
      </button>
      {isFolder && expanded && node.children && (
        <div>
          {node.children
            .sort((a, b) => {
              // Folders first, then alphabetical
              if (a.type === b.type) return a.name.localeCompare(b.name);
              return a.type === 'directory' ? -1 : 1;
            })
            .map((child) => (
              <TreeNodeComponent
                key={child.path}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                onSelect={onSelect}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default function FileTree({ nodes, selectedPath, onSelect }: FileTreeProps) {
  return (
    <div className="py-2 text-sm font-mono">
      {nodes
        .sort((a, b) => {
          if (a.type === b.type) return a.name.localeCompare(b.name);
          return a.type === 'directory' ? -1 : 1;
        })
        .map((node) => (
          <TreeNodeComponent
            key={node.path}
            node={node}
            depth={0}
            selectedPath={selectedPath}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}
